import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import PackageCarousel from '../components/services/PackageCarousel';
import AuditSection from '../components/services/AuditSection';
import LogoMarquee from '../components/services/LogoMarquee';
import ComparisonTable from '../components/services/ComparisonTable';
import AnimateIn from '../components/AnimateIn';

const Services = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Website Design & Development',
      provider: { '@type': 'Organization', name: 'CEXUS', url: 'https://cexus.ca' },
      areaServed: 'CA',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Website Packages',
        itemListElement: [
          { '@type': 'Offer', name: 'Starter', price: '1299', priceCurrency: 'CAD' },
          { '@type': 'Offer', name: 'Business', price: '2499', priceCurrency: 'CAD' },
          { '@type': 'Offer', name: 'Premium Growth', price: '4999', priceCurrency: 'CAD' },
          { '@type': 'Offer', name: 'Enterprise', price: '9999', priceCurrency: 'CAD' },
        ],
      },
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const stagger = { animate: { transition: { staggerChildren: 0.12 } } };
  const fadeInUp = { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 } };

  const menuItems = [
    { label: t('gmMenuPackages'), action: () => scrollTo('ws-packages'), testId: 'gm-packages' },
    { label: t('gmMenuCustomize'), action: () => navigate('/services/customize'), testId: 'gm-customize' },
    { label: t('gmMenuAudit'), action: () => scrollTo('ws-audit'), testId: 'gm-audit' },
    { label: t('gmMenuCompare'), action: () => scrollTo('ws-compare'), testId: 'gm-compare' },
    { label: t('gmMenuStart'), action: () => navigate('/contact'), testId: 'gm-start' },
  ];

  return (
    <div style={{ background: '#050505' }} data-testid="web-services-page">
      {/* Game menu hero */}
      <section className="relative min-h-screen flex items-center px-6 lg:px-8 overflow-hidden" data-testid="ws-hero">
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div
          className="absolute -top-32 right-0 w-[600px] h-[600px] rounded-full blur-[160px] opacity-[0.15] pointer-events-none"
          style={{ background: 'linear-gradient(135deg, #706958, #e2c179)' }}
        />

        <div className="relative z-10 max-w-[1200px] mx-auto w-full py-32 md:py-40">
          <motion.div variants={stagger} initial="initial" animate="animate">
            <motion.p
              className="accent-tag"
              variants={fadeInUp}
              transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
            >
              {t('wsTag')}
            </motion.p>

            <motion.h1
              className="text-4xl md:text-5xl lg:text-[3.75rem] font-bold leading-[1.08] tracking-[-0.02em] text-white max-w-3xl mb-6"
              style={{ fontFamily: "'Syne', sans-serif" }}
              variants={fadeInUp}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              data-testid="ws-hero-title"
            >
              {t('wsTitle')}
            </motion.h1>

            <motion.p
              className="text-base md:text-lg leading-[1.8] max-w-2xl mb-14 text-gray-400"
              variants={fadeInUp}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              data-testid="ws-hero-subtitle"
            >
              {t('wsSubtitle')}
            </motion.p>

            <motion.p
              className="text-[11px] font-semibold tracking-[0.25em] uppercase text-gray-600 mb-2"
              variants={fadeInUp}
              transition={{ duration: 0.8 }}
            >
              {t('gmSelectHint')}
            </motion.p>

            <motion.nav
              className="max-w-2xl"
              variants={fadeInUp}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              data-testid="game-menu"
            >
              {menuItems.map((item, i) => (
                <button key={i} className="gm-btn" onClick={item.action} data-testid={item.testId}>
                  <span className="gm-index">0{i + 1}</span>
                  <span className="flex-1">{item.label}</span>
                  <ChevronRight size={26} className="gm-arrow" />
                </button>
              ))}
            </motion.nav>
          </motion.div>
        </div>
      </section>

      <PackageCarousel />

      <LogoMarquee />

      <AuditSection />

      {/* Customize teaser */}
      <section className="py-20 md:py-28 px-6 lg:px-8" data-testid="customize-teaser">
        <div className="max-w-[1200px] mx-auto">
          <AnimateIn direction="up" duration={0.8}>
            <div
              className="relative overflow-hidden rounded-3xl border border-white/10 p-12 md:p-20 text-center"
              style={{ background: 'radial-gradient(ellipse at 50% 120%, rgba(112,105,88,0.35) 0%, #0a0a0a 60%)' }}
            >
              <p className="accent-tag justify-center">{t('czTeaserTag')}</p>
              <h2
                className="text-3xl md:text-5xl font-bold tracking-[-0.02em] text-white mb-6"
                style={{ fontFamily: "'Syne', sans-serif" }}
                data-testid="customize-teaser-title"
              >
                {t('wsBuilderTitle')}
              </h2>
              <p className="text-gray-400 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-12">
                {t('czTeaserText')}
              </p>
              <div className="flex justify-center">
                <button
                  className="start-btn"
                  onClick={() => navigate('/services/customize')}
                  data-testid="customize-start-btn"
                >
                  {t('czStart')}
                </button>
              </div>
            </div>
          </AnimateIn>
        </div>
      </section>

      <ComparisonTable />

      {/* Final CTA */}
      <section className="py-20 md:py-28 px-6 lg:px-8" data-testid="ws-final-cta">
        <div className="max-w-[1200px] mx-auto">
          <AnimateIn direction="up" duration={0.8}>
            <div
              className="relative overflow-hidden rounded-2xl border border-white/10 p-12 md:p-24 text-center"
              style={{ background: 'linear-gradient(135deg, #0d0d0d 0%, #1a1a1a 50%, #2a2520 100%)' }}
            >
              <div
                className="absolute top-0 right-0 w-80 h-80 rounded-full blur-[120px] opacity-20"
                style={{ background: '#afa283' }}
              />
              <div className="relative z-10 max-w-2xl mx-auto">
                <h2
                  className="text-3xl md:text-5xl font-bold tracking-[-0.02em] text-white mb-6"
                  style={{ fontFamily: "'Syne', sans-serif" }}
                  data-testid="ws-final-title"
                >
                  {t('wsFinalTitle')}
                </h2>
                <p className="text-gray-400 text-base md:text-lg leading-relaxed mb-10">
                  {t('wsFinalText')}
                </p>
                <button
                  className="btn-accent"
                  onClick={() => navigate('/contact')}
                  data-testid="ws-final-cta-btn"
                >
                  {t('wsFinalCta')}
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </AnimateIn>
        </div>
      </section>
    </div>
  );
};

export default Services;
